import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { useIsAuthenticated } from '@/features/auth/store/authStore'
import { useUpdateLocaleMutation } from '@/features/profile/hooks/useProfile'
import { findRouteIdByPath, getLocalizedPath } from '@/shared/routing/routePaths'
import { type Locale, useLocaleStore } from '@/shared/store/localeStore'

/**
 * Lógica común al cambiar el idioma desde cualquier toggle.
 *
 * Persiste en este orden:
 * 1. `localeStore.setLocale` — marca `userToggled=true` para el sync.
 * 2. `i18n.changeLanguage` — la UI cambia al instante.
 * 3. Si la ruta actual está en `routePaths` (bilingual SSG route), navega
 *    a la URL contraparte en el nuevo locale. Si no, se queda donde estaba.
 * 4. Si está autenticado, PATCH /me/profile { locale } para persistir
 *    en backend (emails).
 */
export function useChangeLocale() {
  const { i18n } = useTranslation()
  const { locale, setLocale } = useLocaleStore()
  const isAuthenticated = useIsAuthenticated()
  const updateLocale = useUpdateLocaleMutation()
  const navigate = useNavigate()
  const location = useLocation()

  return {
    locale,
    changeLocale(next: Locale) {
      if (next === locale) return
      setLocale(next)
      void i18n.changeLanguage(next)

      const routeId = findRouteIdByPath(location.pathname)
      if (routeId) {
        void navigate(getLocalizedPath(routeId, next))
      }

      if (isAuthenticated) {
        updateLocale.mutate(next)
      }
    },
  }
}
